import React, {
  useEffect,
  useState,
} from 'react';
import noop from 'lodash/noop';
import {
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  Section,
  SECTIONS,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import TextArea from 'core/components/TextArea';
import {
  useSnapActions,
  VisitDetails,
} from 'views/EMR/stores/snap';
import Name from 'pages/Dashboard/components/Name';
import clsx from 'clsx';
import {
  formatDateTime,
} from 'utils/date';
import {
  useSaveVisitNoteSOAP,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  SaveVisitNoteSOAPRequestDTO,
} from 'dtos';
import {
  useMixpanelTrack,
  useNotify,
} from 'core/hooks';
import {
  useNavigate,
} from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from 'core/icons/link';
import Tooltip from 'core/components/Tooltip';
import {
  normalizeContent,
} from 'views/EMR/Settings/helper';
import Divider from '@mui/material/Divider';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';

export type ReviewSOAPDialogParams = {
  notes: Section[];
  visitDetails: VisitDetails;
};

type Props = ReviewSOAPDialogParams & {
  onClose: () => void;
};

export default function ReviewSOAPDialog({
  notes,
  visitDetails,
  onClose,
}: Props) {
  const mixpanelTrack = useMixpanelTrack();
  const [notedSaved, setNotedSaved] = useState<boolean>(false);
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
  } = useForm<SaveVisitNoteSOAPRequestDTO>();

  const { mutateAsync: saveVisitNoteSOAP, isPending: isSaving } = useSaveVisitNoteSOAP();

  useEffect(() => {
    notes?.forEach((section) => {
      const newValue = normalizeContent(section.text);
      setValue(SECTIONS[section.key], newValue);
    });
  }, [notes]);

  const notify = useNotify();

  const onSubmit = async (data: SaveVisitNoteSOAPRequestDTO) => {
    await saveVisitNoteSOAP({
      ...data,
      encounterId: visitDetails.encounterId,
    });
    notify({
      message: 'SOAP note saved successfully',
      variant: 'success',
    });
    setNotedSaved(true);
    mixpanelTrack('snap-soap-saved-from-review-dialog', {
      href: window.location.href,
    });
  };

  const { setSoapNote } = useSnapActions();
  const navigate = useNavigate();

  const onReviewInVisitNote = async (data: SaveVisitNoteSOAPRequestDTO) => {
    setSoapNote({
      ...data,
      encounterId: visitDetails.encounterId,
    });
    onClose();
    navigate(`/encounters/${visitDetails?.encounterId ?? 0}`);
    mixpanelTrack('snap-soap-reviewed-in-visit-note', {
      href: window.location.href,
    });
  };

  return (
    <Dialog
      open
      title={(
        <div className="flex flex-col gap-1 font-semibold">
          <div className="flex items-center gap-1 text-base">
            <span className="text-gray-500">Review SOAP Note for</span>
            <Name
              name={visitDetails?.patientName}
              classes={{
                root: clsx('!w-fit mr-1 !font-semibold'),
              }}
            />
          </div>
          <div className="text-xs text-gray-500">
            <span>
              Visited on
              {' '}
              <span className="font-semibold">
                {formatDateTime(visitDetails?.visitDateTime)}
              </span>
            </span>
          </div>
        </div>
      )}
      onClose={onClose}
      onConfirm={noop}
      disableEscapeKeyDown
      disableBackdropClick
      titleClassName="!px-5"
      className="populate-form !w-176 !px-0 overflow-hidden max-h-[80vh] flex flex-col"
      autoFocusOptions={{ enabled: false }}
    >
      <div className="flex flex-col gap-form-row px-5 overflow-y-auto flex-1">
        {notedSaved && (
          <div className="flex justify-between items-center bg-emerald-50 px-2 py-1 rounded-sm">
            <div className="text-emerald-800 text-xs font-semibold flex items-center gap-2">
              <CheckCircleIcon className="!h-5 !w-5 !text-inherit" />
              The SOAP note has been saved successfully.
            </div>
            <Tooltip title="Go to Visit Note">
              <Button
                variant="text"
                type="button"
                className="flex items-center gap-2 !text-emerald-800 !text-xs !underline hover:!bg-emerald-100 !py-1 !px-2"
                onClick={() => {
                  onClose();
                  navigate(`/encounters/${visitDetails?.encounterId ?? 0}`);
                }}
              >
                Visit Note
                <LinkIcon className="!h-3 !w-3 !text-inherit" />
              </Button>
            </Tooltip>
          </div>
        )}
        <Controller
          name="additionalHPI"
          control={control}
          render={({ field }) => (
            <Wrapper
              id="additional-hpi"
              label="Additional HPI"
            >
              <TextArea
                id={VisitNoteSOAPSectionNames.AdditionalHPITemplate}
                {...field}
                value={field?.value ?? ''}
                actions={['dictation', 'field']}
                disabled={notedSaved}
              />
            </Wrapper>
          )}
        />
        <Controller
          name="physicalExam"
          control={control}
          render={({ field }) => (
            <Wrapper
              id="physicalExam"
              label="Objective: Physical Exam"
            >
              <TextArea
                id={VisitNoteSOAPSectionNames.PhysicalExamTemplate}
                {...field}
                value={field?.value ?? ''}
                actions={['dictation', 'field']}
                disabled={notedSaved}
              />
            </Wrapper>
          )}
        />
        <Controller
          name="radiology"
          control={control}
          render={({ field }) => (
            <Wrapper
              id="RadiologyTemplate"
              label="Review of Imaging"
            >
              <TextArea
                {...field}
                value={field?.value ?? ''}
                trackingLabel="Radiology"
                id={VisitNoteSOAPSectionNames.RadiologyTemplate}
                disabled={notedSaved}
                actions={['dictation', 'field']}
              />
            </Wrapper>
          )}
        />
        <Controller
          name="additionalAssessment"
          control={control}
          render={({ field }) => (
            <Wrapper
              id="additionalAssessment"
              label="Assessment"
            >
              <TextArea
                id={VisitNoteSOAPSectionNames.AdditionalAssessmentTemplate}
                {...field}
                value={field?.value ?? ''}
                actions={['dictation', 'field']}
                disabled={notedSaved}
              />
            </Wrapper>
          )}
        />
        <Controller
          name="plan"
          control={control}
          render={({ field }) => (
            <Wrapper
              label="Plan"
              id="plan"
            >
              <TextArea
                id={VisitNoteSOAPSectionNames.PlanTemplate}
                {...field}
                value={field?.value ?? ''}
                actions={['dictation', 'field']}
                disabled={notedSaved}
              />
            </Wrapper>
          )}
        />
      </div>
      <div className={clsx('flex gap-4 justify-between px-5', {
        '!justify-end': notedSaved,
      })}
      >
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        {!notedSaved && (
          <div className="flex items-center gap-2">
            <Button
              variant="outlined"
              type="button"
              color="primary"
              className="!h-full !border-secondary disabled:!border-inherit"
              disabled={!isValid}
              onClick={handleSubmit(onReviewInVisitNote)}
            >
              Review In Visit Note
            </Button>
            <Divider orientation="vertical" flexItem />
            <Button
              variant="contained"
              type="button"
              color="primary"
              className="!h-full"
              disabled={!isValid || isSaving}
              onClick={handleSubmit(onSubmit)}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
